import { Injectable, signal } from '@angular/core';
import { isEqual } from 'lodash';
import { GraphMeasure } from '../types/graph-data.interface';
import { CurveStyleResult, Envelope } from '../types/graph-dialog.interface';
import { CustomScaleConfig } from '../types/graphic-metadata.interface';
import { MeasureConfig } from '../types/measure-config.interface';

export interface GraphSavedState {
  graphMeasureIds: number[];
  // XY graphs persist the axis assignment (sort_nr order), so a swap with the
  // same measure set is a savable change — the id set alone cannot see it.
  xyAxisIds: [number, number] | null;
  measureConfigs: Map<number, MeasureConfig>;
  customScales: Map<number, CustomScaleConfig>;
  traceStyles: Map<number | string, Partial<CurveStyleResult>>;
  traceOrder: (number | string)[];
  traceVisibility: boolean[];
  envelopes: Envelope[];
  multiYearYears: string[];
  yearType: string;
}

export function hasUnsavedChanges(current: GraphSavedState, saved: GraphSavedState): boolean {
  const currentIds = [...current.graphMeasureIds].sort((a, b) => a - b);
  const savedIds = [...saved.graphMeasureIds].sort((a, b) => a - b);
  if (!isEqual(currentIds, savedIds)) return true;

  // Same id set but swapped axes is a savable change on XY graphs.
  if (!isEqual(current.xyAxisIds, saved.xyAxisIds)) return true;

  // Comparaison structurelle : un style ou une config rendus par un dialogue
  // n'ont pas l'ordre de clés des métadonnées rouvertes, sans changer de valeur.
  // Plus stricte que JSON sur un point : une clé présente à undefined compte ;
  // les dialogues n'émettent donc que les clés renseignées.
  if (!isEqual(current.measureConfigs, saved.measureConfigs)) return true;
  if (!isEqual(current.customScales, saved.customScales)) return true;
  if (!isEqual(current.traceStyles, saved.traceStyles)) return true;
  if (JSON.stringify(current.traceOrder) !== JSON.stringify(saved.traceOrder)) return true;
  if (JSON.stringify(current.traceVisibility) !== JSON.stringify(saved.traceVisibility)) return true;
  if (JSON.stringify(current.envelopes) !== JSON.stringify(saved.envelopes)) return true;
  if (JSON.stringify(current.multiYearYears) !== JSON.stringify(saved.multiYearYears)) return true;
  return current.yearType !== saved.yearType;
}

function createEmptySavedState(): GraphSavedState {
  return {
    graphMeasureIds: [],
    xyAxisIds: null,
    measureConfigs: new Map(),
    customScales: new Map(),
    traceStyles: new Map(),
    traceOrder: [],
    traceVisibility: [],
    envelopes: [],
    multiYearYears: [],
    yearType: '',
  };
}

@Injectable()
export class GraphStateManager {
  private readonly _savedState = signal<GraphSavedState>(createEmptySavedState());

  readonly savedState = this._savedState.asReadonly();

  saveCurrentState(
    graphMeasures: GraphMeasure[],
    measureConfigs: Map<number, MeasureConfig>,
    customScales: Map<number, CustomScaleConfig>,
    traceStyles: Map<number | string, Partial<CurveStyleResult>>,
    traceOrder: (number | string)[],
    traceVisibility: boolean[],
    envelopes: Envelope[],
    multiYearYears: string[],
    yearType: string,
    xyAxisIds: [number, number] | null = null,
  ): void {
    this._savedState.set({
      graphMeasureIds: graphMeasures.map(m => m.id),
      xyAxisIds,
      measureConfigs: new Map(measureConfigs),
      customScales: new Map(customScales),
      traceStyles: new Map(traceStyles),
      traceOrder: [...traceOrder],
      traceVisibility: [...traceVisibility],
      envelopes: [...envelopes],
      multiYearYears: [...multiYearYears],
      yearType,
    });
  }

  reset(): void {
    this._savedState.set(createEmptySavedState());
  }
}
